<template>
  <form :class="{ 'form': true }">
    <svg class="logo">
      <use xlink:href="#bookero-logo" />
    </svg>

    <div class="wrapper">
      <h1>{{ $t('passwordReset.title') }}</h1>

      <hr>

      <transition-group name="slide-down">
        <message
          v-if="passwordIsEmpty || password2IsEmpty"
          type="error"
          key="msg-1">
          {{ $t('passwordReset.bothFieldsRequired') }}
        </message>

        <message
          v-if="connectionError"
          type="error"
          key="msg-2">
          {{ $t('passwordReset.resetError') }}
        </message>

        <message
          v-if="passwordIsInvalid || password2IsInvalid"
          type="error"
          key="msg-3">
          {{ $t('passwordReset.invalidPasswords') }}
        </message>

        <message
          v-if="passwordsAreDifferent"
          type="error"
          key="msg-4">
          {{ $t('passwordReset.differentPasswords') }}
        </message>

        <message
          v-if="tokenNotExists"
          type="error"
          key="msg-5">
          {{ $t('passwordReset.invalidToken1') }} <router-link to="/password/reminder/">{{ $t('passwordReset.invalidToken2') }}</router-link> {{ $t('passwordReset.invalidToken3') }}
        </message>

        <message
          v-if="statusMessage"
          type="error"
          key="msg-6">
          {{ statusMessage }}
        </message>
      </transition-group>

      <label v-if="!tokenNotExists">
        {{ $t('passwordReset.newPassword') }}
        <input
          type="password"
          v-model="password"
          @keyup="removeEmpty('password')"
          @focus="removeError('password')"
          tabindex="1"
          spellcheck="false"
          :class="{ 'is-invalid': passwordIsInvalid || passwordIsEmpty }"
          :placeholder="$t('passwordReset.yourNewPassword')" />
      </label>

      <label v-if="!tokenNotExists">
        {{ $t('passwordReset.repeatPassword') }}
        <input
          type="password"
          v-model="password2"
          spellcheck="false"
          @keyup="removeEmpty('password2')"
          @focus="removeError('password2')"
          tabindex="2"
          :class="{ 'is-invalid': password2IsInvalid || password2IsEmpty }"
          :placeholder="$t('passwordReset.yourRepeatedPassword')" />
      </label>

      <btn
        v-if="!tokenNotExists"
        type="success wide"
        @click.native="resetPassword"
        :disabled="formSent"
        :tabindex="3">
        <template v-if="!formSent">{{ $t('passwordReset.reset') }}</template>
        <template v-if="formSent">{{ $t('passwordReset.resetting') }}</template>
      </btn>

      <div
        class="footer">
        <span>{{ $t('passwordReset.wantToLogin') }}</span>

        <router-link
          to="/login"
          tabindex="4">
          {{ $t('passwordReset.backToLogin') }}
        </router-link>
      </div>
    </div>

    <btn
      v-if="$root.appLanguage === 'pl'"
      type="with-icon icon-larger"
      title="Switch to english"
      iconName="en"
      iconColor="color-text-light"
      @click.native.prevent="setLang('en')"
      class="switch-lang">
      Switch to english
    </btn>

    <btn
      v-if="$root.appLanguage === 'en'"
      type="with-icon icon-larger"
      title="Zmień na polski"
      iconName="pl"
      iconColor="color-text-light"
      @click.native.prevent="setLang('pl')"
      class="switch-lang">
      Zmień na polski
    </btn>
  </form>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'password-reset',
  data () {
    return {
      password: '',
      password2: '',
      formSent: false,
      passwordIsInvalid: false,
      passwordIsEmpty: false,
      password2IsInvalid: false,
      password2IsEmpty: false,
      connectionError: false,
      passwordsAreDifferent: false,
      tokenNotExists: false,
      statusMessage: ''
    };
  },
  mounted () {
    localStorage.removeItem('bookero-auth-token');
    Vue.prototype.$http.defaults.headers['Authorization'] = 'Bearer null';
    this.$store.commit('config/clearConfig');
    this.$store.commit('global/clear');

    if (!this.$route.params.token) {
      this.tokenNotExists = true;
    }

    if (typeof window.bugsnagClient !== 'undefined') {
      window.bugsnagClient.setUser('0');
    }

    if (typeof window.ga !== 'undefined') {
      window.ga('set', 'dimension1', 0);
    }
  },
  methods: {
    resetPassword (e) {
      e.preventDefault();
      this.connectionError = false;

      if (!this.validate()) {
        return;
      }

      this.formSent = true;
      this.statusMessage = '';

      this.$http.post('/password/reset', {
        'password': this.password,
        'password_confirmation': this.password2,
        'token': this.$route.params.token
      }).then(response => {
        if (response.data.success === 1) {
          this.formSent = false;
          this.$router.push('/login/after-password-reset');
        } else {
          this.statusMessage = response.data.status;
          this.formSent = false;
        }
      }).catch(error => {
        if (error.response.status === 422) {
          this.passwordIsInvalid = true;
          this.password2IsInvalid = true;
        }

        if (!error.response) {
          this.connectionError = true;

          setTimeout(() => {
            this.connectionError = false;
          }, 3000);
        }

        this.formSent = false;
      });
    },
    validate () {
      let formIsValid = true;

      if (this.password.trim() === '') {
        this.passwordIsEmpty = true;
        formIsValid = false;
      }

      if (this.password2.trim() === '') {
        this.password2IsEmpty = true;
        formIsValid = false;
      }

      if (this.password.length < 6) {
        this.passwordIsInvalid = true;
        formIsValid = false;
      }

      if (this.password2.length < 6) {
        this.password2IsInvalid = true;
        formIsValid = false;
      }

      if (this.password !== this.password2) {
        this.passwordsAreDifferent = true;
        formIsValid = false;
      }

      return formIsValid;
    },
    removeError (field) {
      if (field === 'password') {
        this.passwordIsInvalid = false;
        this.passwordsAreDifferent = false;
      }

      if (field === 'password2') {
        this.password2IsInvalid = false;
        this.passwordsAreDifferent = false;
      }
    },
    removeEmpty (field) {
      if (field === 'password') {
        this.passwordIsEmpty = false;
      }

      if (field === 'password2') {
        this.password2IsEmpty = false;
      }
    },
    setLang (newLang) {
      this.$bus.$emit('bookero-set-language', newLang);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.form {
  left: 50%;
  margin: 0;
  max-width: 94%;
  padding: 3.2rem 0;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  transition: opacity .5s ease-out;
  width: 41rem;
  will-change: opacity;

  .logo {
    display: block;
    height: 3.6rem;
    margin: 0 auto 3.2rem auto;
  }

  .wrapper {
    background: $color-bg-lightest;
    border-radius: $border-radius-small;
    padding: 3rem;

    h1 {
      font-size: 2.4rem;
      font-weight: 400;
      margin: 0 0 3rem 0;
      text-align: center;
    }

    hr {
      border: none;
      border-top: 1px solid $color-border;
      margin-bottom: 3rem;
    }

    label {
      display: block;
      font-weight: 400;
      position: relative;

      input {
        background: $color-bg-lightest;
        border: 1px solid $color-border;
        border-radius: $border-radius-small;
        font-size: 1.4rem;
        margin: .5rem 0 1rem 0;
        padding: 1rem;
        width: 100%;

        &::-webkit-input-placeholder {
          color: $color-text-lightest;
        }

        &::-moz-placeholder {
          color: $color-text-lightest;
        }

        &:-ms-input-placeholder {
          color: $color-text-lightest;
        }

        &:-moz-placeholder {
          color: $color-text-lightest;
        }

        &.is-invalid {
          border-color: $color-danger;
          color: $color-danger;
        }
      }

      a {
        font-size: 1.4rem;
        position: absolute;
        right: 0;
        top: .25rem;
      }
    }

    & > .button {
      margin: 1.5rem auto;
    }
  }

  .footer {
    background: $color-bg-medium;
    border-radius: 0 0 $border-radius-small $border-radius-small;
    margin: 3rem -3rem -3rem -3rem;
    padding: 2rem 3rem;
    text-align: center;

    a,
    span {
      display: block;
      line-height: 1.5;
    }
  }
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all .25s ease-out;
}

.slide-down-enter, .slide-down-leave-to {
  height: 0;
  opacity: 0;
}

@media (max-width: 480px) {
  .form {
    .wrapper {
      padding: 2rem;
    }

    .footer {
      margin: 3rem -2rem -2rem -2rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/PasswordReset.vue